import React from "react";
import styled, { css } from "styled-components";

const stepCircleBase = css`
  width: 32px;
  height: 32px;
  border-radius: 16px;
  text-align: center;
  line-height: 32px;
  color: #fff;
  font-family: "A+mfCv-AXISラウンド 50 R StdN", "M PLUS 1p", sans-serif;
`;

// 基本スタイル（灰色）
const StepperStyle = styled.div`
  ${stepCircleBase}
  background: var(--soremo-light-gray);
`;

// 通過後（青色）
const StepperCompleteStyle = styled.div`
  ${stepCircleBase}
  background: var(--soremo-blue);
`;

// 現在位置（アニメーション付き青色）
const StepperCurrentStyle = styled.div`
  ${stepCircleBase}
  background: var(--soremo-blue);

  @keyframes pulse {
    0%,
    100% {
        box-shadow: 0px 0px 12px 0px rgba(0, 154, 206, 0.75);
    }
    50% {
        box-shadow: 0px 0px 12px 0px rgba(0, 154, 206, 0.25);
    }
  }

  animation: pulse 1.5s infinite;
`;

// 円形とラベルをまとめる中コンテナー
const StepperListStyle = styled.div<{
  $totalSteps: number;
  $clickable: boolean;
  $fullWidth: boolean;
}>`
  width: ${props =>
    props.$fullWidth
      ? `calc(100% / ${props.$totalSteps})`
      : `min((100% - 16px) / ${props.$totalSteps} , 756px / ${props.$totalSteps})`};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  top: 50%;
  position: relative;
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")};
`;

// StepperListをまとめる大コンテナー
const StepperContainerStyle = styled.div<{ $fullWidth: boolean }>`
  display: flex;
  margin: 0 auto;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  width: ${props => (props.$fullWidth ? "100%" : "min(100% - 16px, 756px)")};
`;

// 線のスタイル
const StepperLineStyle = styled.div<{ $isCompleted: boolean }>`
  position: absolute;
  width: 100%;
  height: 1px;
  background-color: ${(props) => (props.$isCompleted ? "var(--soremo-blue)" : "var(--soremo-light-gray)")};
  top: 25%; /* 円の中央に配置 */
  left: 50%;
  z-index: -1;
`;

// チェックマークアイコン
const CheckIcon = styled.span.attrs({
  className: "material-symbols-rounded",
})`
  font-size: 20px;
  line-height: 32px;
  color: #fff;
`;

interface StepperProps {
  stepLabels?: string[];
  current?: number;
  showCheckOnComplete?: boolean;
  /**
   * 各ステップをクリックして遷移可能にする場合に渡す。
   * 渡されたステップ番号 (1-origin) を引数に呼び出される。
   * 渡さない（undefined）場合はクリック不可・カーソルもそのまま。
   */
  onSelectStep?: (stepNumber: number) => void;
  /** クリック許可するステップ判定（デフォルトは全て許可）。 */
  isStepClickable?: (stepNumber: number) => boolean;
  /** コンテナ幅いっぱいに全ステップを等分配置する（既定は最大 756px）。 */
  fullWidth?: boolean;
}

const Stepper: React.FC<StepperProps> = ({
  stepLabels = ["コードネーム", "目的物", "予算", "支払い"],
  current = 1,
  showCheckOnComplete = false,
  onSelectStep,
  isStepClickable,
  fullWidth = false,
}) => {
  const totalSteps = stepLabels.length;

  const steps = stepLabels.map((label, index) => {
    const stepNumber = index + 1;
    const isActive = stepNumber === current;
    const isCompleted = stepNumber < current;
    const clickable = !!onSelectStep && (isStepClickable?.(stepNumber) ?? true);
    const handleClick = clickable ? () => onSelectStep?.(stepNumber) : undefined;

    return (
      <StepperListStyle
        key={index}
        $totalSteps={totalSteps}
        $clickable={clickable}
        $fullWidth={fullWidth}
        onClick={handleClick}
      >
        {/* 背景線の配置（最後の要素には表示しない） */}
        {index < stepLabels.length - 1 && (
          <StepperLineStyle $isCompleted={isCompleted} />
        )}
        <div>
          {isCompleted ? (
            <StepperCompleteStyle>
              {showCheckOnComplete ? <CheckIcon>check</CheckIcon> : stepNumber}
            </StepperCompleteStyle>
          ) : isActive ? (
            // 現在地
            <StepperCurrentStyle>
              {stepNumber}
            </StepperCurrentStyle>
          ) : (
            <StepperStyle>
              {stepNumber}
            </StepperStyle>
          )}
        </div>
        <div className="bodytext-11">{label}</div>
      </StepperListStyle>
    );
  });

  return (
    <StepperContainerStyle $fullWidth={fullWidth}>
      {steps}
    </StepperContainerStyle>
  );
};

export default Stepper;
